import Pagination from "@dana/MobiusOverrides/Pagination/Pagination";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTablePresentationProps } from "@insite/mobius/DataTable";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import { DataTableBody, DataTableCell, DataTableRow } from "@insite/mobius/index";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ReactNode, useEffect, useState } from "react";
import { css } from "styled-components";

interface ShortCoupleResultScreenProps {
    series: string;
    clData: number;
    pageNo: number;
    imageUrl?: string;
    imageAltText?: string;
}

interface FindShortCoupleResultStyles {
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    rowWrapper?: InjectableCss;
    formWrapper?: InjectableCss;
    twoColWrapper?: InjectableCss;
    twoColWrapperNoBreakDown?: InjectableCss;
    heading?: TypographyPresentationProps;
    diagramImage?: ImgProps;
    productImage?: ImgProps;
    productPartNumbersWrapper?: InjectableCss;
    productPartNumbersStyles?: TypographyPresentationProps;
    productPartNumbersNoLinkStyles?: TypographyPresentationProps;
    productDescriptionStyles?: TypographyPresentationProps;
    totalResultText?: TypographyPresentationProps;
    attributeTable?: DataTablePresentationProps;
    attributeTableCell?: DataTableCellProps;
    searchButton?: ButtonPresentationProps;
}

export const findShortCoupleResultStyles: FindShortCoupleResultStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
            padding: 16px 16px 16px 16px;
        `,
    },
    contentWrapper: {
        css: css`
            background: ${themeBackgrounds.white};
            padding: 15px 40px 100px 50px;
            height: 430px;
            display: flex;
            flex-direction: column;
            align-items: left;
        `,
    },
    formWrapper: {
        css: css`
            width: 100%;
            max-width: 340px;
            display: flex;
            flex-direction: column;
            gap: 16px;
        `,
    },
    twoColWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 16px;
            width: 100%;
            & > * {
                flex: 1 1 0;
                min-width: 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 12px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 14px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 14px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },

    twoColWrapperNoBreakDown: {
        css: css`
            display: flex;
            flex-direction: row;
            width: 100%;
            cursor: pointer;
            & > *:first-child {
                flex: 0 0 30%;
                max-width: 30%;
            }
            & > *:last-child {
                flex: 0 0 70%;
                max-width: 70%;
                padding: 0 16px;
            }

            &:focus,
            &:focus-visible {
                outline-color: ${themeColors.secondary.danaDarkBlue};
                outline-style: solid;
                outline-width: 1px;
                outline-offset: 2px;
            }
        `,
    },

    rowWrapper: {
        css: css`
            background: ${themeBackgrounds.white};
            display: flex;
            flex-direction: row;
            width: 100%;
            gap: 16px;
            padding: 16px 16px 16px 16px;

            & > .col-3 {
                flex: 0 0 25%;
                max-width: 25%;
            }
            & > .col-6 {
                flex: 0 0 50%;
                max-width: 50%;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            & > .col-3,
                            & > .col-6 {
                                flex: 0 0 100%;
                                max-width: 100%;
                            }
                        `,
                        css`
                            flex-direction: column;
                            & > .col-3,
                            & > .col-6 {
                                flex: 0 0 100%;
                                max-width: 100%;
                            }
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    heading: {
        variant: "h3",
    },
    diagramImage: {
        css: css`
            width: 100%;
            height: auto;
            max-height: 350px;
            object-fit: contain;
        `,
    },
    productImage: {
        css: css`
            width: 100px;
            min-width: 100px;
            max-width: 100px;
            height: auto;
            object-fit: contain;
        `,
    },
    totalResultText: {
        variant: "h4",
        css: css`
            margin-bottom: 0;
        `,
    },
    attributeTable: {
        css: css`
            width: 75%;
            border-collapse: collapse;
            border: 1px solid ${themeBackgrounds.lightGray};
        `,
    },
    attributeTableCell: {
        css: css`
            word-break: break-word;
            white-space: normal;
        `,
    },
    searchButton: {
        variant: "primary",
    },
    productPartNumbersWrapper: {
        css: css`
            a {
                text-decoration: none;
                display: block;
            }
        `,
    },
    productPartNumbersStyles: {
        css: css`
            color: ${themeColors.primary.danaBlue};
            text-decoration: none;
            transition: text-decoration 0.2s;
            &:hover {
                text-decoration: underline;
            }
        `,
        variant: "h4",
    },
    productPartNumbersNoLinkStyles: {
        css: css`
            color: ${themeBackgrounds.darkGray};
        `,
        variant: "h4",
    },
    productDescriptionStyles: {
        css: css`
            color: ${themeColors.primary.richBlack};
        `,
    },
};

type ShortCouple = {
    slipYoke: string;
    slipYokeName: string;
    slipYokeImage: string;
    slipYokeUrl: string;
    yokeShaft: string;
    yokeShaftName: string;
    yokeShaftImage: string;
    yokeShaftUrl: string;
    clclAtMidSlip: number;
    clclCollapsed: number;
    slip: string;
    slipYokeAngle: string;
    yokeShaftAngle: string;
    slipYokeSplineCount: string;
    yokeShaftSplineCount: number;
};

const ShortCoupleResultScreen: React.FC<ShortCoupleResultScreenProps> = ({
    series,
    clData,
    pageNo,
    imageUrl,
    imageAltText,
}) => {
    const styles = findShortCoupleResultStyles;
    const [errorMessage, setErrorMessage] = useState<ReactNode>("");
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(true);
    const [clValue, setClValue] = useState(clData.toString());
    const [selectedSeries, setSelectedSeries] = useState(series);
    const [seriesData, setSeriesData] = useState<string[]>([]);
    const totalResultMessage = translate("Showing {0} results for Couple Shaft");
    const [searchResultMessage, setSearchResultMessage] = useState("");
    const [couples, setCouples] = useState<ShortCouple[]>([]);
    const [totalResult, setTotalResult] = useState(0);

    useEffect(() => {
        const fetchSeries = async () => {
            setShowLoadingOverlay(true);
            try {
                const result = await fetch("/api/v1/findShortCoupleShaft/series");
                if (!result.ok) {
                    throw new Error(result.statusText);
                }
                const data = await result.json();
                setSeriesData(data);
            } catch (error) {
                console.error("Error fetching filters:", error);
            } finally {
                setShowLoadingOverlay(false);
            }
        };

        const fetchResult = async () => {
            setShowLoadingOverlay(true);
            try {
                const result = await fetch(
                    `/api/v1/findShortCoupleShaft/search?series=${series}&installed=${clData}&page=${pageNo}`,
                );
                if (!result.ok) {
                    throw new Error(result.statusText);
                }
                const data = await result.json();
                setTotalResult(data.total);
                setSearchResultMessage(totalResultMessage.replace("{0}", data.total.toString()));
                setCouples(data.couples ?? []);
            } catch (error) {
                console.error("Error fetching filters:", error);
            } finally {
                setShowLoadingOverlay(false);
            }
        };

        fetchSeries();
        fetchResult();
    }, []);

    const search = () => {
        if (!clValue || !selectedSeries) {return;}

        // Only allow valid integer or float numbers (no letters, no extra characters)
        if (!/^\d+(\.\d+)?$/.test(clValue.trim())) {
            setErrorMessage(translate("Please enter a valid number for C-L/C-L Installed."));
            return;
        }

        const params = new URLSearchParams({
            page_no: "1",
            series: selectedSeries,
            cl_data: clValue,
        });
        window.location.search = `?${params.toString()}`;
    };

    const handleSeriesChange: SelectProps["onChange"] = event => {
        setSelectedSeries(event.target.value);
    };

    const changePage = (newPageIndex: number) => {
        if (newPageIndex < 1) {
            newPageIndex = 1;
        }
        const params = new URLSearchParams({
            page_no: newPageIndex.toString(),
            series: series.toString(),
            cl_data: clData.toString(),
        });
        window.location.search = `?${params.toString()}`;
    };

    const handleOpenPartUrl = (partUrl?: string) => {
        if (!partUrl) {return;}
        window.open(partUrl, "_blank", "noreferrer");
    };

    return (
        <LoadingOverlay
            loading={showLoadingOverlay}
            css={css`
                width: 100%;
            `}
        >
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <StyledWrapper {...styles.twoColWrapper}>
                    <StyledWrapper {...styles.contentWrapper}>
                        <Typography {...styles.heading} as="h3">
                            {translate("Find Short Couple Shaft")}
                        </Typography>
                        <StyledWrapper {...styles.formWrapper} className="form-wrapper">
                            <Select
                                label={translate("Series")}
                                name="series"
                                onChange={handleSeriesChange}
                                value={selectedSeries}
                            >
                                <option value="">{translate("Select Series")}</option>
                                {seriesData &&
                                    seriesData.map(s => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                            </Select>
                            <TextField
                                className="cl-data-text-field"
                                id="clDataTextField"
                                value={clValue}
                                onChange={e => {
                                    setErrorMessage("");
                                    setClValue(e.target.value);
                                }}
                                error={errorMessage}
                                label={translate("C-L/C-L Installed")}
                                hint={translate("Examples: 7.5 or 8 or 9.5")}
                            />
                            <Button {...styles.searchButton} onClick={search} disabled={!clValue || !selectedSeries}>
                                {translate("GO")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.contentWrapper}>
                        <Typography {...styles.heading} as="h3">
                            {translate("Assembly Diagram")}
                        </Typography>
                        <Img {...styles.diagramImage} src={imageUrl} altText={imageAltText || ""} />
                    </StyledWrapper>
                </StyledWrapper>

                <Typography {...styles.totalResultText} as="h4">
                    {searchResultMessage}
                </Typography>

                {couples.map((couple, index) => (
                    <StyledWrapper {...styles.rowWrapper} key={index} className="row-wrapper">
                        <div className="col-3">
                            <StyledWrapper
                                {...styles.twoColWrapperNoBreakDown}
                                onClick={() => handleOpenPartUrl(couple.slipYokeUrl)}
                                tabIndex={0}
                                role="button"
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleOpenPartUrl(couple.slipYokeUrl);
                                    }
                                }}
                            >
                                <StyledWrapper>
                                    <Img
                                        {...styles.productImage}
                                        src={couple.slipYokeImage}
                                        altText={couple.slipYokeName}
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.productPartNumbersWrapper}>
                                    {couple.slipYokeUrl ? (
                                        <Typography {...styles.productPartNumbersStyles}>{couple.slipYoke}</Typography>
                                    ) : (
                                        <Typography {...styles.productPartNumbersNoLinkStyles}>
                                            {couple.slipYoke}
                                        </Typography>
                                    )}
                                    <Typography {...styles.productDescriptionStyles} as="span">
                                        {translate("Slip Yoke")}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        </div>
                        <div className="col-3">
                            <StyledWrapper
                                {...styles.twoColWrapperNoBreakDown}
                                onClick={() => handleOpenPartUrl(couple.yokeShaftUrl)}
                                tabIndex={0}
                                role="button"
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleOpenPartUrl(couple.yokeShaftUrl);
                                    }
                                }}
                            >
                                <StyledWrapper>
                                    <Img
                                        {...styles.productImage}
                                        src={couple.yokeShaftImage}
                                        altText={couple.yokeShaftName}
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.productPartNumbersWrapper}>
                                    {couple.yokeShaftUrl ? (
                                        <a href={couple.yokeShaftUrl} target="_blank" rel="noreferrer">
                                            <Typography {...styles.productPartNumbersStyles}>
                                                {couple.yokeShaft}
                                            </Typography>
                                        </a>
                                    ) : (
                                        <Typography {...styles.productPartNumbersNoLinkStyles}>
                                            {couple.yokeShaft}
                                        </Typography>
                                    )}
                                    <Typography {...styles.productDescriptionStyles} as="span">
                                        {translate("Yoke Shaft")}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        </div>
                        <div className="col-6">
                            <DataTable {...styles.attributeTable}>
                                <DataTableBody>
                                    <DataTableRow>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            <Typography as="b">{translate("C-L/C-L at Mid Slip")}</Typography>
                                        </DataTableCell>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            {couple.clclAtMidSlip}
                                        </DataTableCell>
                                    </DataTableRow>
                                    <DataTableRow>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            <Typography as="b">{translate("C-L/C-L Collapsed")}</Typography>
                                        </DataTableCell>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            {couple.clclCollapsed}
                                        </DataTableCell>
                                    </DataTableRow>
                                    <DataTableRow>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            <Typography as="b">{translate("Total Slip")}</Typography>
                                        </DataTableCell>
                                        <DataTableCell {...styles.attributeTableCell}>{couple.slip}</DataTableCell>
                                    </DataTableRow>
                                    <DataTableRow>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            <Typography as="b">{translate("Slip Yoke Angle")}</Typography>
                                        </DataTableCell>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            {couple.slipYokeAngle}
                                        </DataTableCell>
                                    </DataTableRow>
                                    <DataTableRow>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            <Typography as="b">{translate("Yoke Shaft Angle")}</Typography>
                                        </DataTableCell>
                                        <DataTableCell {...styles.attributeTableCell}>
                                            {couple.yokeShaftAngle}
                                        </DataTableCell>
                                    </DataTableRow>
                                </DataTableBody>
                            </DataTable>
                        </div>
                    </StyledWrapper>
                ))}

                {totalResult > 0 && (
                    <Pagination
                        resultsCount={totalResult}
                        currentPage={pageNo}
                        resultsPerPage={10}
                        resultsPerPageOptions={[]}
                        onChangePage={changePage}
                        onChangeResultsPerPage={() => {}}
                        pageSizeCookie="FindShortCouple-PageSize"
                        data-test-selector="findShortCouple_pagination"
                    />
                )}
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default ShortCoupleResultScreen;
